import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { citiesAPI } from '../services/api';
import { MapPin, ArrowRight, Users, BadgeCheck, TrendingUp } from 'lucide-react';

const CITY_SERVICES = [
  'Femme de ménage Casablanca', 'Ménage à domicile Casablanca', 'Jardinier Casablanca',
  'Bricoleur Casablanca', 'Déménageur Casablanca', 'Plombier Casablanca',
  'Électricien Casablanca', 'Baby-sitter Casablanca', 'Peintre Casablanca',
];

export const CitiesSection = () => {
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);

  useEffect(() => {
    citiesAPI.getAll().then(res => setCities(res.data)).catch(() => {});
  }, []);

  const mainCities = cities.filter((c) => c.is_main || c.isMain);
  const otherCities = cities.filter((c) => !c.is_main && !c.isMain);

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main city services */}
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
          Nos services à Casablanca
        </h2>
        <div className="flex flex-wrap gap-2 mb-12">
          {CITY_SERVICES.map((service, i) => (
            <button
              key={i}
              className="text-sm text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-full px-4 py-2 transition-colors"
            >
              {service}
            </button>
          ))}
        </div>

        {/* Cities grid */}
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
          Les prestataires Khadam Express dans nos top villes
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {mainCities.map((city) => (
            <div key={city.id} className="border border-gray-100 rounded-xl p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={18} className="text-blue-500" />
                <h3 className="text-lg font-bold text-gray-900">{city.name}</h3>
              </div>
              <ul className="space-y-1.5">
                {['Femme de ménage', 'Bricoleur', 'Plombier', 'Déménageur', 'Baby-sitter'].map((service, idx) => (
                  <li key={idx}>
                    <button className="text-sm text-blue-600 hover:text-blue-700 hover:underline transition-colors">
                      {service} {city.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Other cities */}
        {otherCities.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Autres villes</h3>
            <div className="flex flex-wrap gap-2">
              {otherCities.map((city) => (
                <button
                  key={city.id}
                  className="text-sm text-gray-600 hover:text-blue-600 bg-gray-50 hover:bg-blue-50 rounded-full px-4 py-2 transition-colors"
                >
                  {city.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export const BecomeProviderSection = () => {
  const navigate = useNavigate();

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex flex-col md:flex-row">
            {/* Text side */}
            <div className="flex-1 p-8 md:p-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Devenez prestataire sur Khadam Express
              </h2>
              <p className="text-gray-600 mb-6 max-w-lg">
                Inscrivez-vous et trouvez de nouveaux clients, développez votre notoriété et commencez à optimiser vos revenus dès maintenant.
              </p>
              <button
                onClick={() => navigate('/devenir-prestataire')}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-full px-8 py-3 transition-colors text-sm mb-6"
              >
                Devenir prestataire
              </button>
              <div className="flex flex-wrap gap-4">
                {['Particulier', 'Pro indépendant', 'Agence'].map((type) => (
                  <span key={type} className="text-sm text-gray-500 bg-gray-50 rounded-full px-4 py-1.5">
                    {type}
                  </span>
                ))}
              </div>
            </div>

            {/* Stats illustration */}
            <div className="flex-shrink-0 md:w-[360px] bg-gradient-to-br from-blue-50 to-green-50 p-8 flex flex-col justify-center">
              <div className="space-y-5">
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <TrendingUp size={20} className="text-green-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Montant total gagné</div>
                      <div className="text-xl font-bold text-gray-900">12 000 DH</div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-green-600 font-medium">+28% par rapport au mois dernier</div>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Users size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Nouveau job remporté</div>
                      <div className="text-sm font-semibold text-gray-900">Ménage à domicile - Casablanca</div>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <BadgeCheck size={20} className="text-emerald-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Nouveau paiement</div>
                      <div className="text-lg font-bold text-emerald-600">+540 DH</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
